import {createContext, useContext, useEffect, useRef, useState} from 'react';

const USE_LOCAL_SERVER = false


const API_BASE = "/api";
const LOGSERVER = true;


//
// The basis serverAPI for both local and remote servers
//
export async function serverAPI(action, username, data = null) {

    // Case we use a local server
    if (USE_LOCAL_SERVER) {

        const allSaves = JSON.parse(localStorage.getItem('serverSaves') || '{}');

        // Load action
        if (action === 'load') {
            return allSaves[username] || null;
        }

        // Save action (only fields specified)
        else if (action === 'save') {
            if (!allSaves[username]) allSaves[username] = {}
            Object.assign(allSaves[username], data);
            localStorage.setItem("serverSaves", JSON.stringify(allSaves))
            return true;
        }

        // getSessionIds action
        else if (action === 'getSessionIds') {
            return allSaves[username]?.sessionIds || []
        }
    }

    // Distant server
    else {

        // Load action
        if (action === "load") {
            const res = await fetch(
                `${API_BASE}/load.php?username=${encodeURIComponent(username)}`
            );
            if (res.status === 404) return null; // aligné sur la version locale
            if (!res.ok) throw new Error(`load failed (${res.status})`);
            return await res.json();
        }

        // Save action
        if (action === "save") {
            const res = await fetch(`${API_BASE}/save.php`, {
                method: "POST",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify({username, data}),
            });
            if (!res.ok) throw new Error(`save failed (${res.status})`);
            const json = await res.json();
            return json?.success === true;
        }

        // getSessionIds action
        if (action === "getSessionIds") {
            const res = await fetch(
                `${API_BASE}/getSessionIds.php?username=${encodeURIComponent(username)}`
            );
            if (res.status === 404) return []; // aligné sur la version locale
            if (!res.ok) throw new Error(`getSessionIds failed (${res.status})`);
            return await res.json(); // attendu: tableau
        }
    }
}

// GenerateID function
function generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : ((r & 0x3) | 0x8);
        return v.toString(16);
    });
}

// Get sessionID (generates it if needed)
function getSessionID() {
    let res = localStorage.getItem('sessionId')
    if (res === null || res === undefined) {
        res = generateUUID();
        localStorage.setItem('sessionId', res);
    }
    return res;
}

//
// Function to merge the Stats values (Server and Local)
// The values are of the form [nSuccesses, nFailures]
// - objServer is the full data on the server
// - objServerUpdates an object to update according to the updates that have to be performed
//
// The localStorage is updated if needed
//
// returns [renderObjLocalNeeded, serverNeedsUpdate];
// - updatedKeys
// - renderObjLocalNeeded : true if localStorage update was necessary
//
function mergeStats(objServer, objServerUpdates) {

    // Some inits
    let serverKeyListUpdates = new Set()
    let renderObjLocalNeeded = false;

    // We first build the local stats data object
    let objLocal = {}
    for (const [key, value] of Object.entries(localStorage)) {
        if (key.includes("Stats"))
            objLocal[key] = JSON.parse(value)
    }

    //
    // We are first merging the stats keys that exist locally (this is the toughest part of the merge procedure)
    // Loop for merging the statsKeys of the objLocal successively
    //
    for (const statsKey of Object.keys(objLocal)) {

        if (!(statsKey in objServer)) {
            serverKeyListUpdates.add(statsKey)
            continue
        }

        // The local stats to be merged
        let statsLocal = objLocal[statsKey]
        let statsServer = objServer[statsKey]

        //
        // We just make a first pass to see wether statsKey should be in serverKeyListUpdates
        // due to missing subkeys (NOT due to different successes or failures !)
        //
        // Loop on the text to be sure all the texts of statsLocal are in statsServer
        for (const text of Object.keys(statsLocal)) {
            if (!statsServer?.[text]) {
                serverKeyListUpdates.add(statsKey)
                break;
            }
            for (const key of Object.keys(statsLocal[text])) {
                if (!statsServer[text]?.[key]) {
                    serverKeyListUpdates.add(statsKey)
                    break;
                }
            }
        }

        //
        // Now the real loop to update the values of statsLocal
        //
        // We start from obj2 and update it using obj1
        // The structure of these objects are
        // keys = hebrew text
        // for each key obj[text] is an object with several keys 'hf', 'fh', 'lt', .... and the values are [nSuccesses, nFailures]
        let statsLocalUpdateNeeded = false
        Object.keys(statsServer).forEach(text => {
            if (!statsLocal?.[text]) {
                statsLocal[text] = statsServer[text];
                statsLocalUpdateNeeded = true;
            } else {
                Object.keys(statsServer[text]).forEach(key => {
                    if (!statsLocal[text]?.[key]) {
                        statsLocal[text][key] = statsServer[text][key];
                        statsLocalUpdateNeeded = true;
                    } else {
                        const [a1, b1] = statsServer[text][key];
                        const [a2, b2] = statsLocal[text][key];
                        if (a1 !== a2 || b1 !== b2) {
                            const a = Math.max(a1, a2);
                            const b = Math.max(b1, b2);
                            console.log(statsLocal, text,key,a,b)
                            statsLocal[text][key] = [a, b];
                            if (a2 !== a || b2 !== b)
                                statsLocalUpdateNeeded = true;
                            if (a1 !== a || b1 !== b)
                                serverKeyListUpdates.add(statsKey)
                        }
                    }
                })
            }
        })
        renderObjLocalNeeded = renderObjLocalNeeded || statsLocalUpdateNeeded

        // statsLocalUpdate if needed
        if (statsLocalUpdateNeeded) {
            localStorage.setItem(statsKey, JSON.stringify(objLocal[statsKey]))
        }

    }

    // Prepare the object for serverUpdate
    for (const key of serverKeyListUpdates) {
        objServerUpdates[key] = objLocal[key]
    }

    // We add to objLocal the objServer statsKeys that are eventually missing
    for (const [key, value] of Object.entries(objServer)) {
        if (key.includes("Stats") && !(key in objLocal)) {
            objLocal[key] = value
            localStorage.setItem(key,JSON.stringify(value))
            renderObjLocalNeeded = true
        }
    }

    return renderObjLocalNeeded;
}

// prefsKeys
const prefKeys = [["repeatFactor", "1"]]

// Add local preferences key values to obj
function addLocalPrefs2Obj(obj) {
    for (const [key, defaultValue] of prefKeys) {
        obj[key] = JSON.parse(localStorage.getItem(key) || defaultValue)
    }
}

// Add obj preferences locally
function addObjPrefsLocally(obj) {
    let renderNeeded = false
    for (const [key, defaultValue] of prefKeys) {
        const value = obj[key] ? JSON.stringify(obj[key]) : JSON.stringify(defaultValue)
        renderNeeded = renderNeeded || localStorage.getItem(key) !== value
        localStorage.setItem(key, value)
    }
    return renderNeeded
}

// Add local courses values to obj
function addLocalCourses2Obj(obj) {
    obj['coursesLearned'] = JSON.parse(localStorage.getItem('coursesLearned') || "{}")
}

// Add obj preferences locally
function addObjCoursesLocally(obj) {
    localStorage.setItem("coursesLearned", JSON.stringify(obj?.coursesLearned || {}))
    return true
}


//*********************************************
//
// THE SERVER CONTEXT
//
//*********************************************

const ServerContext = createContext(null);

//
// List of events the servercontext (via addEvent2ServerQueue) is able to handle :
//
//  - "ChangeUser"  : the user has been changed
//  - "UpdateStats" : the stats fields have to be merged between local and server
//  - "UpdatePrefs" : the local prefs fields have changed (they should override the server ones)
//  - "UpdateCourses" : the local coursesLearned field has changed (they should override the server ones)
//  - "OverrideServerStats" : override all the Stats fields of the server with the local ones
//

export function ServerProvider({children}) {

    // The useState's for rendering
    const [statsRender, setStatsRender] = useState(0)
    const triggerStatsRender = () => {
        console.log("TriggerStatsRender111")
        setStatsRender(t => t+1)
    }
    const [coursesRender, setCoursesRender] = useState(true)
    const triggerCoursesRender = () => {
        setCoursesRender(t => !t)
    }
    const [prefsRender, setPrefsRender] = useState(true)
    const triggerPrefsRender = () => {
        setPrefsRender(t => !t)
    }


    // The useState with the queue of events
    const [eventServerQueue, setEventServerQueue] = useState(JSON.parse(localStorage.getItem("eventServerQueue")) || [])
    useEffect(() => {
        localStorage.setItem("eventServerQueue", JSON.stringify(eventServerQueue))
    }, [eventServerQueue])

    // What actions are allowed according to events left in the queue ?
    const [isAllowedChangeUser, setIsAllowedChangeUser] = useState(true);
    const [isAllowedGame, setIsAllowedGame] = useState(true);
    const [isAllowedCoursesLearned, setIsAllowedCoursesLearned] = useState(true);
    const [isAllowedOverrideServerStats, setIsAllowedOverrideServerStats] = useState(true);
    useEffect(() => {
        setIsAllowedOverrideServerStats(eventServerQueue.length === 0)
        setIsAllowedChangeUser(!(eventServerQueue.includes("updateStats") || eventServerQueue.includes("updateCourses")))
        setIsAllowedGame(!(eventServerQueue.includes("changeUser")))
        setIsAllowedCoursesLearned(!(eventServerQueue.includes("changeUser")))
    }, [eventServerQueue])

    // Adding an event to the event queue
    const addEvent2ServerQueue = (event) => {
        if (LOGSERVER)
            console.log("LOGSERVER : Adding event '", event, "' to queue :", eventServerQueue)
        if (event === "changeUser" && eventServerQueue.length !== 0) return
        if (event === "overrideServerStats" && eventServerQueue.length !== 0) return
        if (event !== "changeUser" && eventServerQueue?.[0] === "changeUser") return
        if (eventServerQueue.includes(event)) return;
        setEventServerQueue([...eventServerQueue, event])
        // In the case of overrideServerStats --> we need to sync right away since we are on the setup page
        if (event === "overrideServerStats")
            setSyncNow(value => !value)
    }

    // Clean local storage when changing user
    const cleanLocalStorageChangeUser = () => {
        for (const key of Object.keys(localStorage)) {
            if (key.includes("Stats")) {
                localStorage.removeItem(key)
            }
        }
        localStorage.removeItem("coursesLearned")
        localStorage.removeItem("hash")
    }

    //
    // The function that runs the server synchronization
    //
    // Returns a flag indicating render is needed
    //

    const lockSync = useRef(false)
    const serverSync = async () => {

        if (lockSync.current === true) return
        lockSync.current = true

        console.log(">>> LOGSERVER : enter serverSync")

        const username = localStorage.getItem("currentUsername")
        const sessionID = getSessionID();
        let dataOwned = null
        let renderStatsObjLocalNeeded = false;
        let renderPrefsObjLocalNeeded = false;
        let renderCoursesObjLocalNeeded = false;
        let renderObjLocalNeeded = false;

        const events = eventServerQueue;

        let remoteSessionIds = null;
        let remoteData = null

        // Test whether we need to load all the data from server
        let flagNeedAllData = events.includes("changeUser") || events.includes("updateStats");
        if (!flagNeedAllData) {
            remoteSessionIds = await serverAPI("getSessionIds", username)
            remoteSessionIds = remoteSessionIds ? remoteSessionIds : []
            if (LOGSERVER)
                console.log("LOGSERVER : Loading remoteSessionIds : ", remoteSessionIds)
            flagNeedAllData = !(remoteSessionIds.includes(sessionID)) && !events.includes("overrideServerStats")
        }

        // Load them if needed
        if (flagNeedAllData) {
            remoteData = await serverAPI("load", username)
            remoteData = remoteData ? remoteData : {}
            if (LOGSERVER)
                console.log("LOGSERVER : Loading all remote data : ", remoteData)
            remoteSessionIds = remoteData?.sessionIds
            remoteSessionIds = remoteSessionIds ? remoteSessionIds : []
        }

        // Ownership
        dataOwned = remoteSessionIds.includes(sessionID);
        let dataOrphan = remoteSessionIds.length === 0;
        if (LOGSERVER)
            console.log("LOGSERVER : dataOwned=", dataOwned, "dataOrphan=", dataOrphan)

        // Case of a ChangeUser, the new username is in the "futureUsername" localStorage
        if (events.includes("changeUser")) {

            localStorage.removeItem("hash");

            // Copy remoteData to localStorage
            for (const [key, value] of Object.entries(remoteData)) {
                if (key !== "sessionIds")
                    localStorage.setItem(key, JSON.stringify(value))
            }
            console.log("LOGSERVER (changeUser event): load remote data and assign locally")
            renderObjLocalNeeded = true;

            if (!dataOwned) {
                remoteSessionIds.push(sessionID)
                console.log("LOGSERVER (changeUser event): add co-ownership on server")
            }
            await serverAPI("save", username, {"sessionIds": remoteSessionIds})
        }
        // case of overrideServerStats event
        else if (eventServerQueue.includes("overrideServerStats")) {
            // Get all the local stats 
            let data2Upload = {}
            for (const key of Object.keys(localStorage)) {
                if (key.includes("Stats")) {
                    data2Upload[key] = JSON.parse(localStorage[key])
                }
            }
            data2Upload["sessionIds"] = [sessionID]

            if (LOGSERVER)
                    console.log("LOGSERVER : Override server stats with local stats")
            await serverAPI("save", username, data2Upload)
            if (LOGSERVER) {
                console.log("LOGSERVER : upload data", data2Upload)
            }
            renderStatsObjLocalNeeded = true
        }

        // Other events (or no event)
        else {
            // A variable to store the data that will be needed to upload on the server
            let data2Upload = {}

            // If !dataOwned or updateStats ==> we need to do a stats merge
            if (!dataOwned || events.includes("updateStats")) {
                if (LOGSERVER)
                    renderStatsObjLocalNeeded = mergeStats(remoteData, data2Upload)
                console.log("LOGSERVER : mergeStats (renderStatsObjLocalNeeded = ",
                    renderStatsObjLocalNeeded, ") data2Upload = ", data2Upload)
            }

            // If UpdatePrefs event or dataOrphan ownership we add preferences to data2Upload
            if (events.includes("updatePrefs") || dataOrphan) {
                addLocalPrefs2Obj(data2Upload)
                if (LOGSERVER)
                    console.log("LOGSERVER : add local prefs to data2Upload = ", data2Upload)
            }
            // Otherwise if no ownership we copy remote prefs locally
            else if (!dataOwned) {
                renderPrefsObjLocalNeeded = addObjPrefsLocally(remoteData)
                if (LOGSERVER)
                    console.log("LOGSERVER : set local prefs with remote prefs")
            }

            // If updateCourses we add it to data2Upload
            if (events.includes("updateCourses") || dataOrphan) {
                addLocalCourses2Obj(data2Upload)
                if (LOGSERVER)
                    console.log("LOGSERVER : add local coursesLearned to data2Upload = ", data2Upload)
            }
            // Otherwise if no ownership we copy the remote coursesLearned locally
            else if (!dataOwned) {
                renderPrefsObjLocalNeeded = addObjCoursesLocally(remoteData)
                if (LOGSERVER)
                    console.log("LOGSERVER : set local coursesLearned with remote coursesLearned")
            }

            // If data2Upload is empty we have to set co-ownership
            if (Object.keys(data2Upload).length === 0) {
                if (!remoteSessionIds.includes(sessionID)) {
                    data2Upload.sessionIds = remoteSessionIds
                    data2Upload.sessionIds.push(sessionID)
                    if (LOGSERVER)
                        console.log("LOGSERVER : set co-ownership because no upload needed")
                }
            }

            // Otherwise, we take ownership if not already have it
            else {
                if (remoteSessionIds.length !== 1 || remoteSessionIds[0] !== sessionID)
                    data2Upload.sessionIds = [sessionID]
                if (LOGSERVER)
                    console.log("LOGSERVER : set ownership because upload occurred")
            }

            //
            // If data2Upload is not empty we have to send it to the server
            //
            if (Object.keys(data2Upload).length !== 0) {
                if (localStorage.getItem("hash")) data2Upload["hash"] = "1"
                await serverAPI("save", username, data2Upload)
                if (LOGSERVER) {
                    console.log("LOGSERVER : upload data", data2Upload)
                }
            }
        }

        setEventServerQueue([]);

        if (renderObjLocalNeeded) {
            renderPrefsObjLocalNeeded = true
            renderStatsObjLocalNeeded = true
            renderCoursesObjLocalNeeded = true
        }

        if (LOGSERVER) {
            console.log("LOGSERVER renderStatsObjLocalNeeded=", renderStatsObjLocalNeeded,
                ", renderCoursesObjLocalNeeded=", renderCoursesObjLocalNeeded, ", renderPrefsObjLocalNeeded=", renderPrefsObjLocalNeeded)
            console.log("<<< LOGSERVER : leaves serverSync")
        }

        if (renderPrefsObjLocalNeeded) triggerPrefsRender()
        if (renderStatsObjLocalNeeded) triggerStatsRender()
        if (renderCoursesObjLocalNeeded) triggerCoursesRender()

        lockSync.current = false
    }

    // A mechanism to run a sync
    const [syncNow, setSyncNow] = useState(false)
    useEffect(() => {
        async function sync() {await serverSync();}
        sync()
    },[syncNow])



    //
    // Defining the context
    //
    return (
        <ServerContext.Provider value={{
            addEvent2ServerQueue,
            serverSync,
            isAllowedChangeUser,
            isAllowedGame,
            isAllowedCoursesLearned,
            isAllowedOverrideServerStats,
            statsRender,
            coursesRender,
            prefsRender,
            cleanLocalStorageChangeUser,
            serverAPI
        }}>
            {children}
        </ServerContext.Provider>
    );
}

export function useServer() {
    return useContext(ServerContext);
}

